import { Condition, ConditionAttributes } from '~/models/Condition';
import { TrackerAttributes } from '~/models/Tracker';
import EventApiClient from '~/apiClients/EventApiClient';

export interface EventAttributes {
  id: number;
  trackerId: number;
  type?: number;
  label: string;
  delay: number;
  priority: number;
  tracker: TrackerAttributes;
  originalId?: number;
  conditions: Array<ConditionAttributes>;
  createdAt: string;
  updatedAt?: string;
}

export interface EventCreationAttributes {
  label: string;
  trackerId: number;
  type: number;
}

export interface EventUpdateAttributes {
  id: number;
  label: string;
}

export class Event {
  constructor(private readonly attributes: EventAttributes) {
  }

  get conditions(): Array<Condition> {
    this.attributes.conditions.sort((a, b) => a.position - b.position);
    return this.attributes.conditions.map((conditionAttributes: ConditionAttributes) => new Condition(conditionAttributes));
  }

  get id(): number {
    return this.attributes.id;
  }

  get label(): string {
    return this.attributes.label;
  }

  get delay(): number {
    return this.attributes.delay;
  }

  get priority(): number {
    return this.attributes.priority;
  }

  get trackerId(): number {
    return this.attributes.trackerId;
  }

  get originalId(): number|undefined {
    return this.attributes.originalId;
  }

  async clone(): Promise<Event> {
    return await Event.getApiClient().cloneEvent(this.id);
  }

  static async create(trackerId: number, label: string, delay: number, type: number = 0, priority: number = 0): Promise<Event> {
    return this.getApiClient().createEvent({
      trackerId,
      label,
      delay,
      type,
      priority,
    } as EventCreationAttributes);
  }

  static async findAllByTrackerId(trackerId: number): Promise<Event[]> {
    return await this.getApiClient().provideEvents(trackerId);
  }

  static async findById(id: number): Promise<Event> {
    return await this.getApiClient().provideEvent(id);
  }

  static async findSuggestedLabels(): Promise<Array<string>> {
    return await this.getApiClient().provideSuggestedLabels();
  }

  async delete(id: number): Promise<any> {
    return Event.getApiClient().deleteEvent(id);
  }

  async update(label: string, delay: number, priority: number): Promise<Event> {
    return await Event.getApiClient().updateEvent({
      id: this.id,
      label,
      delay,
      priority,
    } as EventUpdateAttributes);
  }

  private static getApiClient(): EventApiClient {
    return new EventApiClient();
  }
}
